import type { UserProfile } from '@/lib/types'
import { Avatar } from './Avatar'
import { cn } from '@/lib/utils'

interface Props {
  /** User ids to display, in render order (assignee usually first). */
  userIds: string[]
  /** Lookup table for display name + avatar URL — same one the card has. */
  profilesById: Record<string, UserProfile>
  /** When set, the matching avatar is highlighted with an accent ring as
   *  the "primary" / responsible user (vs other watchers/subscribers). */
  assigneeId?: string | null
  /** Maximum number of visible avatars before collapsing the rest into a
   *  « +N » pill. Defaults to 5 (typical GitHub PR reviewer stack size). */
  max?: number
  /** Avatar pixel size. */
  size?: 'sm' | 'md'
}

const SIZE_CLASSES = {
  sm: { wrap: 'h-4 w-4', text: 'text-[7px]', overlap: '-ml-1' },
  md: { wrap: 'h-5 w-5', text: 'text-[8px]', overlap: '-ml-1.5' },
} as const

/** Overlapping avatar stack à la GitHub / Linear / FaceTime.
 *
 *  Renders up to `max` avatars (assignee first if present), each
 *  overlapping the previous by ~40 %. If `userIds.length` exceeds `max`,
 *  the overflow collapses into a small « +N » pill matching the avatar
 *  size, also overlapping. The assignee gets an accent ring to mark
 *  them as the responsible user.
 *
 *  Per-avatar names are exposed via the native `title` tooltip (the
 *  browser shows them on a short hover delay). The richer member-list
 *  popover that used to open here was removed — it overloaded the
 *  interface; the full roster is still accessible via the « Membres »
 *  button in the top bar. */
export function AvatarStack({
  userIds,
  profilesById,
  assigneeId = null,
  max = 5,
  size = 'md',
}: Props) {
  // De-dup ids while preserving order. If an assignee is in the list,
  // bubble them to the front so they're the leftmost avatar and the
  // ring cue is most visible.
  const ordered = [...new Set(userIds)]
  if (assigneeId && ordered.includes(assigneeId)) {
    ordered.splice(ordered.indexOf(assigneeId), 1)
    ordered.unshift(assigneeId)
  }
  if (ordered.length === 0) return null

  const visible = ordered.slice(0, max)
  const overflow = Math.max(0, ordered.length - visible.length)
  const sizes = SIZE_CLASSES[size]

  // Note: no onPointerDown / onClick stopPropagation here. Originally
  // AvatarStack stopped clicks to prevent the parent card from opening
  // the detail modal when the user just wanted to see avatar tooltips.
  // But that very stop also blocked the interactive SubscriberPicker
  // from ever receiving its own click (the picker wraps AvatarStack in
  // a <button> and was never reached). Each interactive consumer now
  // handles its own propagation strategy — SubscriberPicker stops at
  // its outer wrapper; non-interactive fallback just accepts that a
  // click on an avatar also opens the card (acceptable).
  return (
    <div className="flex items-center">
      {visible.map((id, i) => {
        const isAssignee = id === assigneeId
        return (
          <span
            key={id}
            className={cn(
              'relative inline-flex rounded-full ring-2 ring-[var(--color-surface)]',
              // First avatar has no overlap; subsequent ones slide left.
              i > 0 && sizes.overlap,
              // Stacking order: assignee should NOT be hidden by the next
              // avatar. Reverse z-index so the leftmost (= primary) stays
              // on top.
            )}
            style={{ zIndex: visible.length - i }}
          >
            <Avatar
              profile={profilesById[id]}
              userId={id}
              className={cn(
                sizes.wrap,
                // Accent ring around the assignee, brighter than the
                // neutral white ring around the others.
                isAssignee &&
                  'ring-1 ring-offset-1 ring-offset-[var(--color-surface)] ring-[var(--color-accent)]',
              )}
              textClassName={sizes.text}
            />
          </span>
        )
      })}
      {overflow > 0 && (
        <span
          title={`+${overflow} ${overflow === 1 ? 'autre' : 'autres'}`}
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-3)] font-semibold text-[var(--color-text-dim)] ring-2 ring-[var(--color-surface)]',
            sizes.wrap,
            sizes.overlap,
            sizes.text,
          )}
          style={{ zIndex: 0 }}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
